var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
/**
 * Secondary navigation component for screens within the scaffold.
 * Provides a simple nav drawer for sub-navigation within a page/screen.
 * Typically used when viewing details of a specific entity (e.g., Company, Contact).
 *
 * ## Usage
 * ```html
 * <kr-screen-nav
 *   label="Acme Corporation"
 *   subLabel="Company"
 *   .items=${[
 *     { id: 'overview', label: 'Overview', url: '/companies/123/overview' },
 *     { id: 'contacts', label: 'Contacts', url: '/companies/123/contacts' },
 *     { id: 'activity', label: 'Activity', url: '/companies/123/activity' },
 *   ]}
 *   activeId="overview"
 *   .actions=${[
 *     { id: 'edit', label: 'Edit', variant: 'flat' },
 *     { id: 'preview', label: 'Preview', href: '/s/companies/123', target: '_blank' },
 *     { id: 'archive', label: 'Archive' },
 *     { id: 'delete', label: 'Delete' },
 *     { id: 'export', label: 'Export' },
 *   ]}
 * >
 *   <div>Screen content here</div>
 * </kr-screen-nav>
 * ```
 *
 * Actions render in a footer at the bottom of the nav column. With more than
 * `maxVisibleActions` (default 5), the overflow collapses into a "More" dropdown.
 * Listen for `action-click` to handle a click: `e.detail.action` is the action id. An action with
 * an `href` renders as a link and navigates on its own; it does not fire `action-click`.
 *
 * @slot - The main screen content
 *
 * @property {string} label - Main label (e.g., entity name like "Acme Corporation")
 * @property {string} subLabel - Sub label (e.g., entity type like "Company")
 * @property {KRScreenNavItem[]} items - Navigation items as JSON array
 * @property {string} activeId - Currently active item ID
 * @property {KRScreenNavAction[]} actions - Action buttons rendered in the nav footer
 * @property {number} maxVisibleActions - Max inline actions before collapsing into a "More" dropdown (default 5)
 *
 * @fires action-click - When an action button (inline or overflow) without an `href` is clicked; detail is `{ action: id }`
 */
export class KRScreenNav extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Main label (e.g., entity name like "Acme Corporation")
         */
        this.label = '';
        /**
         * Sub label (e.g., entity type like "Company")
         */
        this.subLabel = '';
        /**
         * Navigation items
         */
        this.items = [];
        /**
         * Currently active item ID. Auto-detected from the URL by default.
         * Can be set explicitly by the parent to override.
         */
        this.activeId = '';
        /**
         * Action buttons rendered in the nav footer.
         */
        this.actions = [];
        /**
         * Max number of inline action buttons before the overflow collapses into a
         * "More" dropdown. When actions exceed this count, the last visible slot is
         * used for the "More" button so the total footer button count never exceeds
         * this value.
         */
        this.maxVisibleActions = 5;
        this.handlePopstate = () => {
            this.syncActiveFromUrl();
        };
    }
    connectedCallback() {
        super.connectedCallback();
        // Defer so property bindings (.items) have been applied
        requestAnimationFrame(() => this.syncActiveFromUrl());
        window.addEventListener('popstate', this.handlePopstate);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('popstate', this.handlePopstate);
    }
    /**
     * Handles click events on navigation items.
     *
     * Dispatches a cancelable `nav-item-click` custom event.
     * If a listener calls `preventDefault()` on the custom event, native browser
     * navigation is prevented. Otherwise, the default `<a href>` navigation
     * proceeds normally.
     *
     * The `@krubble/angular` package provides `KRScreenNavDirective` which
     * automatically listens for this event and handles navigation using
     * Angular's router.
     *
     * @param e - The original click event
     * @param item - The nav item that was clicked
     */
    handleNavItemClick(e, item) {
        const navEvent = new CustomEvent('nav-item-click', {
            detail: { item },
            bubbles: true,
            composed: true,
            cancelable: true,
        });
        this.dispatchEvent(navEvent);
        // If a listener called preventDefault(), prevent the native navigation
        if (navEvent.defaultPrevented) {
            e.preventDefault();
        }
        this.activeId = item.id;
    }
    syncActiveFromUrl() {
        const pathname = window.location.pathname;
        const match = this.items.find((item) => {
            if (!item.url) {
                return false;
            }
            // Absolute URL — exact match
            if (item.url.startsWith('/')) {
                return pathname === item.url;
            }
            // Relative URL — match against the end of the pathname
            return pathname.endsWith('/' + item.url);
        });
        if (match) {
            this.activeId = match.id;
        }
    }
    handleActionClick(e, action) {
        // A link action navigates on its own through the kr-button's anchor; it doesn't fire action-click.
        if (action.href) {
            return;
        }
        this.dispatchEvent(new CustomEvent('action-click', {
            detail: { action: action.id },
            bubbles: true,
            composed: true,
        }));
    }
    // Fired by the "More" kr-button when an overflow option is chosen; detail is { id, label }.
    handleMoreSelect(e) {
        this.dispatchEvent(new CustomEvent('action-click', {
            detail: { action: e.detail.id },
            bubbles: true,
            composed: true,
        }));
    }
    render() {
        const hasOverflow = this.actions.length > this.maxVisibleActions;
        // When collapsing, the last visible slot is the "More" button, so only
        // show the first (maxVisibleActions - 1) actions inline.
        let inlineActions = this.actions;
        let overflowOptions = [];
        if (hasOverflow) {
            inlineActions = this.actions.slice(0, this.maxVisibleActions - 1);
            // Everything past the inline slots goes into the "More" dropdown.
            overflowOptions = this.actions.slice(this.maxVisibleActions - 1).map((action) => ({
                id: action.id,
                label: action.label,
                href: action.href,
                hrefTarget: action.target,
            }));
        }
        return html `
      <nav class="nav">
        ${this.label ? html `
          <div class="nav-header">
            <div class="nav-header__text">
              ${this.subLabel ? html `<div class="nav-header__subtitle">${this.subLabel}</div>` : nothing}
              <div class="nav-header__title">${this.label}</div>
            </div>
          </div>
        ` : nothing}
        <div class="nav-content">
          <div class="nav-items">
            ${this.items.map((item) => html `
                <a
                  class=${classMap({
            'nav-item': true,
            'nav-item--active': item.id === this.activeId
        })}
                  href=${item.url || '#'}
                  @click=${(e) => this.handleNavItemClick(e, item)}
                >
                  ${item.label}
                </a>
              `)}
          </div>
        </div>
        ${this.actions.length ? html `
          <div class="nav-actions">
            ${inlineActions.map((action) => html `
                <kr-button
                  class="nav-actions__button"
                  variant=${action.variant || 'outline'}
                  size="small"
                  href=${action.href || nothing}
                  target=${action.target || nothing}
                  @click=${(e) => this.handleActionClick(e, action)}
                >
                  ${action.label}
                </kr-button>
              `)}
            ${hasOverflow ? html `
              <kr-button
                class="nav-actions__button"
                variant="outline"
                size="small"
                .options=${overflowOptions}
                @option-select=${this.handleMoreSelect}
              >
                More
              </kr-button>
            ` : nothing}
          </div>
        ` : nothing}
      </nav>
      <main class="content">
        <div class="content__main">
          <slot></slot>
        </div>
      </main>
    `;
    }
}
KRScreenNav.styles = css `
    :host {
      display: flex;
      height: 100%;
    }

    .nav {
      width: 200px;
      background: #f3f7fcbf;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
    }

    .nav-header {
      padding: 20px 16px 16px;
    }

    .nav-header__text {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .nav-header__title {
      font-size: 20px;
      font-weight: 600;
      color: #111827;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.3;
    }

    .nav-header__subtitle {
      font-size: 12px;
      font-weight: 400;
      color: rgb(0 0 0 / 70%);
      line-height: 1;
    }

    .nav-content {
      flex: 1;
      overflow-y: auto;
      padding: 0 0 12px;
    }

    .nav-items {
      display: flex;
      flex-direction: column;
      padding: 0 8px;
    }

    .nav-item {
      display: block;
      padding: 0 12px;
      height: 32px;
      line-height: 32px;
      letter-spacing: 0.13px;
      color: rgb(32, 33, 36);
      text-decoration: none;
      font-size: 13px;
      font-weight: 400;
      font-family: inherit;
      border-radius: 8px;
      transition: all 0.15s ease;
    }

    .nav-item:hover {
      background: rgba(0, 0, 0, 0.04);
      color: #163052;
    }

    .nav-item--active {
      background: #e1e9f6;
      color: #1a2332;
      font-weight: 700;
    }

    .nav-actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px 16px;
      border-top: 1px solid rgba(0, 0, 0, 0.08);
      flex-shrink: 0;
    }

    .nav-actions__button {
      width: 100%;
    }

    .content {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    .content__main {
      flex: 1;
      overflow-y: auto;
    }
  `;
__decorate([
    property({ type: String })
], KRScreenNav.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRScreenNav.prototype, "subLabel", void 0);
__decorate([
    property({ type: Array })
], KRScreenNav.prototype, "items", void 0);
__decorate([
    property({ type: String })
], KRScreenNav.prototype, "activeId", void 0);
__decorate([
    property({ type: Array })
], KRScreenNav.prototype, "actions", void 0);
__decorate([
    property({ type: Number })
], KRScreenNav.prototype, "maxVisibleActions", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-screen-nav')) {
    customElements.define('kr-screen-nav', KRScreenNav);
}
//# sourceMappingURL=screen-nav.js.map